import apiService from '../../Services/APIService';
import { checkAPIResponse } from '../../Utils/APIResponseUtils';

export const setPostAPI = async (postData) => {
    try {
        let photoVal = '[]';
        if (Array.isArray(postData.photos)) {
            photoVal = JSON.stringify(postData.photos.filter(Boolean));
        } else if (typeof postData.photos === 'string' && postData.photos.trim()) {
            const trimmed = postData.photos.trim();
            if (trimmed.startsWith('[')) {
                photoVal = trimmed;
            } else {
                photoVal = JSON.stringify([trimmed]);
            }
        }

        const preparedPostData = {
            PostID: postData.postID || 0,
            Title: postData.title || '',
            Detail: postData.detail || '',
            PostTypeID: postData.postTypeID || 'A',
            AudienceTypeID: postData.audienceTypeID || '1',
            IsDeleted: postData.isDeleted || 0,
            Photos: photoVal,
            Document: postData.document || '',
            ReactionTypeID: postData.reactionTypeID || '1'
        };

        const result = await apiService.sendChange(15, 'set', preparedPostData);
        checkAPIResponse(result);

        return result;
    } catch (error) {
        console.error('Set post API error:', error);
        throw error;
    }
};

export const deletePostAPI = async (post) => {
    let contextSet = false;
    try {
        const targetAgencyID = Number(post?.CreatedAgencyID || 0);
        if (targetAgencyID && apiService?.agencyContext?.forAgencyID !== targetAgencyID) {
            apiService.setAgencyContext(targetAgencyID);
            contextSet = true;
        }

        return await setPostAPI({
            postID: post.PostID,
            title: post.Title,
            detail: post.Detail,
            postTypeID: post.Type,
            photos: post.PhotoPath || '',
            isDeleted: 1
        });
    } finally {
        if (contextSet) {
            apiService.clearAgencyContext();
        }
    }
};

export const getPostsAPI = async (viewAll = false) => {
    try {
        const result = await apiService.getData(15, 'getPosts', { ViewAll: viewAll ? 1 : 0 });
        checkAPIResponse(result);

        if (result?.updatePackage && typeof result.updatePackage === 'string') {
            try {
                const parsed = JSON.parse(result.updatePackage);
                return parsed?.Table1 || [];
            } catch (parseError) {
                console.error('Error parsing getPosts updatePackage:', parseError);
                return [];
            }
        }

        return [];
    } catch (error) {
        console.error('Get posts API error:', error);
        throw error;
    }
};

export const getPostDetailsAPI = async (postID) => {
    try {
        const result = await apiService.getData(15, 'getPostDetails', { PostID: postID });
        checkAPIResponse(result);

        if (result?.updatePackage && typeof result.updatePackage === 'string') {
            try {
                const parsed = JSON.parse(result.updatePackage);
                const summary = parsed?.Table1?.[0] || null;
                const comments = parsed?.Table2 || [];
                const creatorMobile = parsed?.Table3?.[0]?.CreatorMobile || '';
                return { summary, comments, creatorMobile };
            } catch (parseError) {
                console.error('Error parsing getPostDetails updatePackage:', parseError);
                return { summary: null, comments: [], creatorMobile: '' };
            }
        }

        return { summary: null, comments: [], creatorMobile: '' };
    } catch (error) {
        console.error('Get post details API error:', error);
        throw error;
    }
};

const parseFeedDetailPackage = (result) => {
    if (!result?.updatePackage || typeof result.updatePackage !== 'string') {
        return { summary: null, comments: [] };
    }
    try {
        const parsed = JSON.parse(result.updatePackage);
        return {
            summary: parsed?.Table1?.[0] || null,
            comments: parsed?.Table2 || []
        };
    } catch (parseError) {
        console.error('Error parsing feed action updatePackage:', parseError);
        return { summary: null, comments: [] };
    }
};

const feedActionAPI = async ({ postID, changeType, commentID = 0, comment = '' }) => {
    const result = await apiService.sendChange(15, 'action', {
        PostID: postID,
        ChangeType: changeType,
        CommentID: commentID,
        Comment: comment
    });
    checkAPIResponse(result);
    return result;
};

export const likePostAPI = async (postID) => {
    try {
        return await feedActionAPI({ postID, changeType: 'LIKE' });
    } catch (error) {
        console.error('Like post API error:', error);
        throw error;
    }
};

export const reportPostAPI = async (postID) => {
    try {
        return await feedActionAPI({ postID, changeType: 'REPORT' });
    } catch (error) {
        console.error('Report post API error:', error);
        throw error;
    }
};

export const addCommentAPI = async (postID, commentText, parentCommentID = 0) => {
    try {
        const text = typeof commentText === 'object' && commentText !== null ? (commentText.Val || '') : String(commentText || '');
        const pid = typeof commentText === 'object' && commentText !== null ? (commentText.PID || 0) : parentCommentID;
        const result = await feedActionAPI({ postID, changeType: 'COMMENT', commentID: pid, comment: text });
        return parseFeedDetailPackage(result);
    } catch (error) {
        console.error('Add comment API error:', error);
        throw error;
    }
};

export const editCommentAPI = async (postID, commentID, newVal) => {
    try {
        return await feedActionAPI({ postID, changeType: 'EDITCMNT', commentID, comment: String(newVal || '') });
    } catch (error) {
        console.error('Edit comment API error:', error);
        throw error;
    }
};

export const deleteCommentAPI = async (postID, commentID) => {
    try {
        return await feedActionAPI({ postID, changeType: 'DLTCMNT', commentID });
    } catch (error) {
        console.error('Delete comment API error:', error);
        throw error;
    }
};
